/**
 * notifications — a generic, app-wide notification system.
 *
 * This is intentionally NOT translation-specific. Any feature can push a
 * notification (translation today; publish/SCORM/comments tomorrow) and the
 * header bell + toasts render it automatically by its `status`.
 *
 *   notify()  → add a persistent notification (shows in the bell, counts unread)
 *   update()  → patch an existing one in place (e.g. progress ticks)
 *   remove()  → drop one
 *   toast()   → fire an ephemeral 3-second toast (not stored in the bell)
 *
 * Mounted once at app root, ABOVE feature providers that produce notifications.
 *
 * Server notifications (13 Oct 2026 — migration 14, /api/orgs notifications-*): a course assigned,
 * a due-date reminder, an overdue course. Loaded when signed in (organisations on), refreshed every
 * minute and when the tab comes back, and merged with the in-browser ones (translation progress…).
 * Read / unread / delete on a server notification is saved on the server, so every device agrees.
 */
import {
  createContext, useContext, useState, useCallback, useEffect, useRef, type ReactNode,
} from 'react'
import { createPortal } from 'react-dom'
import { Bell, Loader2, CheckCircle2, AlertCircle, Info, X, BookOpen, Clock, CalendarX, UserPlus, Languages, Sparkles, Presentation, Copy, type LucideIcon } from 'lucide-react'
import { useAuth } from '@clerk/clerk-react'
import { useNavigate } from 'react-router-dom'
import { FEATURE_ORGANISATIONS } from '@/lib/featureFlags'
import { orgApi, type ServerNotification } from '@/lib/orgApi'

export type NotifStatus = 'info' | 'progress' | 'success' | 'error'

export interface NotifAction {
  label: string
  onClick: () => void
  variant?: 'primary' | 'ghost' | 'danger'
}

export interface AppNotification {
  id: string
  /** Grouping/source tag, e.g. 'translation'. Free-form for forward use. */
  category: string
  status: NotifStatus
  title: string
  body?: string
  /** 0–100, rendered as a bar when status === 'progress'. */
  progress?: number
  actions?: NotifAction[]
  createdAt: number
  read: boolean
  /** Kept on the server (org_notifications) — read / delete go there too. */
  server?: boolean
  /** Where clicking it goes (an app path). */
  link?: string | null
  /** A server notification about an invitation: which one (its Join / Decline come from OrgProvider). */
  inviteId?: string
  /** …and the organisation it is to (for an invitation that arrived after the app loaded). */
  inviteOrgId?: string
  /** A server notification about an ownership transfer: which one (Accept / Decline come from OrgProvider). */
  transferId?: string
}

interface ToastItem {
  id: string
  status: NotifStatus
  title: string
  body?: string
  actions?: NotifAction[]
}

interface NotificationsCtx {
  notifications: AppNotification[]
  unreadCount: number
  /** Add (or upsert by id) a persistent notification. Returns its id. An upsert keeps it read. */
  notify: (n: Omit<AppNotification, 'createdAt' | 'read'> & { id?: string }) => string
  update: (id: string, patch: Partial<AppNotification>) => void
  /** Take one off the bell (a server one is deleted there, so it stays gone everywhere). */
  remove: (id: string) => void
  /** The buttons for each waiting invitation, by invitation id — shown on its server notification. */
  setInviteActions: (next: Record<string, NotifAction[]> | ((prev: Record<string, NotifAction[]>) => Record<string, NotifAction[]>)) => void
  /** Buttons for an invitation the app didn't know about when it loaded (16 Oct 2026, D3): one sent
   *  while the person is signed in reaches the bell on its next refresh, and gets Join / Decline here. */
  setInviteResponder: (fn: ((orgId: string, inviteId: string) => NotifAction[]) | null) => void
  /** Accept / Decline for an ownership transfer's notification (20 Oct 2026). */
  setTransferResponder: (fn: ((transferId: string) => NotifAction[]) | null) => void
  markAllRead: () => void
  clearAll: () => void
  /** Flip one between read and unread. */
  toggleRead: (id: string) => void
  /** Mark one read (opening it). */
  markRead: (id: string) => void
  /** Fetch the server's list now (the bell opening, a tab coming back). */
  refresh: () => void
  /** Fire an ephemeral 3-second toast (not stored in the bell). */
  toast: (t: Omit<ToastItem, 'id'>) => void
}

const Ctx = createContext<NotificationsCtx | null>(null)

export function useNotifications(): NotificationsCtx {
  const v = useContext(Ctx)
  if (!v) throw new Error('useNotifications must be used within NotificationProvider')
  return v
}

let _seq = 0
const newId = (p: string) => `${p}-${Date.now().toString(36)}-${(_seq++).toString(36)}`

/** A server notification, as the bell shows it. */
function fromServer(n: ServerNotification): AppNotification {
  return {
    id: n.id, category: n.kind, title: n.title, body: n.body ?? undefined, link: n.link, read: n.read, server: true,
    createdAt: Date.parse(n.createdAt) || Date.now(),
    status: n.kind === 'overdue' ? 'error' : n.kind === 'translation_done' ? 'success' : 'info',
    ...(n.inviteId ? { inviteId: n.inviteId, ...(n.orgId ? { inviteOrgId: n.orgId } : {}) } : {}),
    ...(n.transferId ? { transferId: n.transferId } : {}),
  }
}

export function NotificationProvider({ children }: { children: ReactNode }) {
  const [local, setLocal] = useState<AppNotification[]>([])
  const [server, setServer] = useState<AppNotification[]>([])
  const [toasts, setToasts] = useState<ToastItem[]>([])
  const { isSignedIn } = useAuth()
  // Invitations (14 Oct 2026): their notifications live on the server like every other kind, so read
  // and dismissed hold in every browser. Only the Join / Decline buttons come from the browser —
  // OrgProvider knows how to answer each waiting invitation.
  const [inviteActions, setInviteActions] = useState<Record<string, NotifAction[]>>({})
  const [inviteResponder, setResponder] = useState<{ fn: (orgId: string, inviteId: string) => NotifAction[] } | null>(null)
  const setInviteResponder = useCallback((fn: ((orgId: string, inviteId: string) => NotifAction[]) | null) => setResponder(fn ? { fn } : null), [])
  const [transferResponder, setTResponder] = useState<{ fn: (transferId: string) => NotifAction[] } | null>(null)
  const setTransferResponder = useCallback((fn: ((transferId: string) => NotifAction[]) | null) => setTResponder(fn ? { fn } : null), [])

  // ── The server's list: on sign-in, every minute, and when the tab comes back ──
  // A list that was asked for BEFORE a read / delete was sent can arrive after it and would put the
  // old state back; `writes` counts the changes so such a list is dropped (the next one is right).
  const writes = useRef(0)
  const refresh = useCallback(() => {
    if (!FEATURE_ORGANISATIONS || !isSignedIn) return
    const at = writes.current
    orgApi.notificationsList()
      .then(r => { if (writes.current === at) setServer(r.notifications.map(fromServer)) })
      .catch(() => { /* keep what we have */ })
  }, [isSignedIn])
  useEffect(() => {
    if (!FEATURE_ORGANISATIONS || !isSignedIn) return
    refresh()
    const t = setInterval(refresh, 60_000)
    const onVisible = () => { if (document.visibilityState === 'visible') refresh() }
    document.addEventListener('visibilitychange', onVisible)
    return () => { clearInterval(t); document.removeEventListener('visibilitychange', onVisible) }
  }, [isSignedIn, refresh])

  const notify = useCallback<NotificationsCtx['notify']>((n) => {
    const id = n.id ?? newId('ntf')
    setLocal(prev => {
      const existing = prev.find(x => x.id === id)
      const next: AppNotification = { ...n, id, createdAt: existing?.createdAt ?? Date.now(), read: false }
      return existing ? prev.map(x => (x.id === id ? next : x)) : [next, ...prev]
    })
    return id
  }, [])

  const update = useCallback<NotificationsCtx['update']>((id, patch) => {
    setLocal(prev => prev.map(x => (x.id === id ? { ...x, ...patch } : x)))
  }, [])

  const isServer = useCallback((id: string) => server.some(x => x.id === id), [server])

  const remove = useCallback<NotificationsCtx['remove']>((id) => {
    if (isServer(id)) {
      writes.current++
      setServer(prev => prev.filter(x => x.id !== id))
      orgApi.notificationsDelete([id]).catch(refresh)
    } else setLocal(prev => prev.filter(x => x.id !== id))
  }, [isServer, refresh])

  const setRead = useCallback((id: string, read: boolean) => {
    if (isServer(id)) {
      writes.current++
      setServer(prev => prev.map(x => (x.id === id ? { ...x, read } : x)))
      orgApi.notificationsRead([id], read).catch(refresh)
    } else setLocal(prev => prev.map(x => (x.id === id ? { ...x, read } : x)))
  }, [isServer, refresh])
  const toggleRead = useCallback((id: string) => {
    const n = [...local, ...server].find(x => x.id === id)
    if (n) setRead(id, !n.read)
  }, [local, server, setRead])
  const markRead = useCallback((id: string) => setRead(id, true), [setRead])

  const markAllRead = useCallback(() => {
    setLocal(prev => prev.some(x => !x.read) ? prev.map(x => ({ ...x, read: true })) : prev)
    if (server.some(x => !x.read)) {
      writes.current++
      setServer(prev => prev.map(x => ({ ...x, read: true })))
      orgApi.notificationsReadAll().catch(refresh)
    }
  }, [server, refresh])

  /** Everything off the bell — except work still running, which keeps its progress and Cancel. */
  const clearAll = useCallback(() => {
    setLocal(prev => prev.filter(x => x.status === 'progress'))
    if (server.length) {
      writes.current++
      setServer([])
      orgApi.notificationsDelete('all').catch(refresh)
    }
  }, [server, refresh])

  const toast = useCallback<NotificationsCtx['toast']>((t) => {
    const id = newId('tst')
    setToasts(prev => [...prev, { ...t, id }])
    setTimeout(() => setToasts(prev => prev.filter(x => x.id !== id)), 3000)
  }, [])
  const dismissToast = useCallback((id: string) => {
    setToasts(prev => prev.filter(x => x.id !== id))
  }, [])

  const withButtons = server.map(n => {
    if (n.transferId && transferResponder) return { ...n, actions: transferResponder.fn(n.transferId) }
    if (!n.inviteId) return n
    const actions = inviteActions[n.inviteId] ?? (n.inviteOrgId && inviteResponder ? inviteResponder.fn(n.inviteOrgId, n.inviteId) : undefined)
    return actions ? { ...n, actions } : n
  })
  const notifications = [...local, ...withButtons].sort((a, b) => b.createdAt - a.createdAt)
  const unreadCount = notifications.filter(n => !n.read).length

  return (
    <Ctx.Provider value={{ notifications, unreadCount, notify, update, remove, setInviteActions, setInviteResponder, setTransferResponder, markAllRead, clearAll, toggleRead, markRead, refresh, toast }}>
      {children}
      <NotificationToaster toasts={toasts} onDismiss={dismissToast} />
    </Ctx.Provider>
  )
}

// ── Shared bits ───────────────────────────────────────────────────────────────

function StatusIcon({ status }: { status: NotifStatus }) {
  if (status === 'progress') return <Loader2 className="w-4 h-4 animate-spin text-[#9333EA]" />
  if (status === 'success') return <CheckCircle2 className="w-4 h-4 text-green-600" />
  if (status === 'error') return <AlertCircle className="w-4 h-4 text-red-500" />
  return <Info className="w-4 h-4 text-gray-400" />
}

function ActionButtons({ actions, afterClick }: { actions?: NotifAction[]; afterClick?: () => void }) {
  if (!actions || actions.length === 0) return null
  return (
    <div className="mt-2 flex items-center gap-3">
      {actions.map((a, i) => (
        <button
          key={i}
          onClick={() => { a.onClick(); afterClick?.() }}
          className={
            a.variant === 'danger' ? 'text-xs font-medium text-gray-400 hover:text-red-500'
            : a.variant === 'ghost' ? 'text-xs font-medium text-gray-500 hover:text-gray-700'
            : 'text-xs font-medium text-[#9333EA] hover:text-[#7E22CE]'
          }
        >
          {a.label}
        </button>
      ))}
    </div>
  )
}

// ── Ephemeral toasts ──────────────────────────────────────────────────────────

function NotificationToaster({ toasts, onDismiss }: { toasts: ToastItem[]; onDismiss: (id: string) => void }) {
  if (toasts.length === 0) return null
  return (
    <div className="fixed top-20 right-4 z-50 flex flex-col items-end gap-2 w-80 max-w-[calc(100vw-2rem)]">
      {toasts.map(t => (
        <div key={t.id} className="w-full rounded-xl border border-gray-200 bg-white shadow-lg p-3.5 text-sm">
          <div className="flex items-start gap-2.5">
            <div className="mt-0.5 shrink-0"><StatusIcon status={t.status} /></div>
            <div className="min-w-0 flex-1">
              <div className="font-medium text-gray-900 truncate">{t.title}</div>
              {t.body && <div className="text-xs text-gray-500 truncate">{t.body}</div>}
              <ActionButtons actions={t.actions} afterClick={() => onDismiss(t.id)} />
            </div>
            <button onClick={() => onDismiss(t.id)} className="shrink-0 text-gray-300 hover:text-gray-500" aria-label="Dismiss">
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      ))}
    </div>
  )
}

// ── Header bell + side panel ─────────────────────────────────────────────────
// A right-hand panel (5 Oct 2026), not a small dropdown: titles wrap instead of being cut
// off ("Someone invited you to join Conve…"), and actions are real buttons.

/** “Just now” · “4 min ago” · “3 h ago” · “Yesterday” · “5 days ago” · then the date. */
function timeAgo(t: number, now: number): string {
  const m = Math.floor((now - t) / 60000)
  if (m < 1) return 'Just now'
  if (m < 60) return `${m} min ago`
  const h = Math.floor(m / 60)
  if (h < 24) return `${h} h ago`
  const d = Math.floor(h / 24)
  if (d === 1) return 'Yesterday'
  if (d < 7) return `${d} days ago`
  return new Date(t).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', ...(new Date(t).getFullYear() !== new Date(now).getFullYear() ? { year: 'numeric' } : {}) })
}
const exact = (t: number) => new Date(t).toLocaleString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })


/**
 * Each kind of notification (13 Oct 2026 — the notifications handoff): its icon, the tile's tint and
 * ink, and the label of its main button. Local kinds (translation, custom-slide…) are background
 * tasks: one notification that runs, then turns ready or failed.
 */
const KIND: Record<string, { icon: LucideIcon; bg: string; fg: string; action?: string }> = {
  course_assigned:  { icon: BookOpen, bg: '#F3EDFC', fg: '#7A3BD9', action: 'Start' },
  due_reminder:     { icon: Clock, bg: '#FDF3E4', fg: '#B45309', action: 'Continue' },
  overdue:          { icon: CalendarX, bg: '#FDECEA', fg: '#DC2626', action: 'Start now' },
  'org-invite':     { icon: UserPlus, bg: '#E6F0FA', fg: '#1D6FB8' },
  org_invite:       { icon: UserPlus, bg: '#E6F0FA', fg: '#1D6FB8' },
  translation:      { icon: Languages, bg: '#E3F3EE', fg: '#0F766E' },
  translation_done: { icon: Languages, bg: '#E3F3EE', fg: '#0F766E', action: 'Review' },
  'custom-slide':   { icon: Sparkles, bg: '#EFE9FE', fg: '#7C3AED' },
  'pptx-import':    { icon: Presentation, bg: '#FDEFE3', fg: '#C2610C' },
  clone:            { icon: Copy, bg: '#E0F2FE', fg: '#0284C7' },
}
const PLAIN = { icon: Info, bg: '#F1EFF4', fg: '#6B6775' }
/** A button for a notification that has somewhere to go but no button of its kind (20 Oct 2026):
 *  the server's join, access and re-invite requests, and “You're in …”. Named for where it goes. */
function linkLabel(link: string): string {
  if (link.startsWith('/org/members?tab=waiting')) return 'Review request'
  if (link.startsWith('/org/members')) return 'Open Members'
  if (link.startsWith('/org/invite')) return 'Open Invite people'
  if (link.startsWith('/org/reports')) return 'Open Reports'
  if (link.startsWith('/learn/') || link.startsWith('/courses/')) return 'Open course'
  return 'Open'
}

/** Today, or earlier — by the calendar, not the last 24 hours. */
const isToday = (t: number, now: number) => new Date(t).toDateString() === new Date(now).toDateString()

export function NotificationBell({ className }: { className?: string }) {
  const { notifications, unreadCount, markAllRead, clearAll, remove, markRead, refresh } = useNotifications()
  const navigate = useNavigate()
  const [open, setOpen] = useState(false)
  const [tab, setTab] = useState<'all' | 'unread'>('all')
  // Clear all has no undo, so it asks once: the first click arms it, the second clears.
  const [confirmClear, setConfirmClear] = useState(false)
  const clearable = notifications.some(n => n.status !== 'progress')
  // The times keep moving while the panel is open (“Just now” → “1 min ago”).
  const [now, setNow] = useState(() => Date.now())

  useEffect(() => {
    if (!open) return
    refresh()
    const t = setInterval(() => setNow(Date.now()), 30_000)
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false) }
    document.addEventListener('keydown', onKey)
    return () => { clearInterval(t); document.removeEventListener('keydown', onKey) }
  }, [open, refresh])

  const shown = notifications.filter(n => tab === 'all' || !n.read)
  const sections: Array<[string, AppNotification[]]> = [
    ['In progress', shown.filter(n => n.status === 'progress')],   // was “Working on it” (24 Oct 2026)
    ['Today', shown.filter(n => n.status !== 'progress' && isToday(n.createdAt, now))],
    ['Earlier', shown.filter(n => n.status !== 'progress' && !isToday(n.createdAt, now))],
  ]

  return (
    <div className={`relative ${className ?? ''}`}>
      <button
        onClick={() => { setNow(Date.now()); setConfirmClear(false); setOpen(o => !o) }}
        className="relative grid h-[38px] w-[38px] place-items-center rounded-[10px] text-[#4A4653] transition-colors hover:bg-[#F4F2F6]"
        aria-label={unreadCount ? `Notifications, ${unreadCount} unread` : 'Notifications'}
      >
        <Bell className="h-5 w-5" strokeWidth={1.6} />
        {unreadCount > 0 && (
          <span className="absolute right-[1px] top-[2px] grid h-[17px] min-w-[17px] place-items-center rounded-full bg-[#7A3BD9] px-1 text-[10.5px] font-semibold text-white"
            style={{ boxShadow: '0 0 0 2px #fff' }}>
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {open && createPortal(
        <div className="fixed inset-0 z-[100]" role="dialog" aria-modal="true" aria-label="Notifications">
          <div className="absolute inset-0" style={{ background: 'rgba(28,26,34,.28)' }} onClick={() => setOpen(false)} />
          <aside className="absolute bottom-0 right-0 top-0 flex w-[440px] max-w-full flex-col bg-white max-[640px]:w-full"
            style={{ boxShadow: '-16px 0 40px rgba(28,26,34,.14)', color: '#1C1A22' }}>
            {/* Header */}
            <div className="flex flex-none items-center gap-2.5 px-6 pb-3.5 pt-[22px]">
              <h2 className="m-0 shrink-0 whitespace-nowrap text-[21px] font-semibold tracking-[-.01em]">Notifications</h2>
              {unreadCount > 0 && <span className="shrink-0 whitespace-nowrap rounded-full bg-[#F3EDFC] px-[9px] py-0.5 text-[12.5px] font-semibold text-[#5B21B6]">{unreadCount} new</span>}
              <button onClick={() => setOpen(false)} aria-label="Close" className="ml-auto grid h-[34px] w-[34px] place-items-center rounded-lg text-[#4A4653] hover:bg-[#F4F2F6]">
                <X className="h-[15px] w-[15px]" strokeWidth={1.8} />
              </button>
            </div>
            {/* Tabs */}
            <div className="flex flex-none items-center gap-1.5 border-b border-[#ECE9F0] px-6 pb-3.5">
              {([['all', 'All'], ['unread', unreadCount ? `Unread · ${unreadCount}` : 'Unread']] as const).map(([k, l]) => (
                <button key={k} onClick={() => setTab(k)} aria-pressed={tab === k}
                  className="h-8 shrink-0 whitespace-nowrap rounded-full border px-[13px] text-[13px] font-semibold"
                  style={tab === k ? { background: '#1C1A22', borderColor: '#1C1A22', color: '#fff' } : { background: '#fff', borderColor: '#DDD8E4', color: '#1C1A22' }}>
                  {l}
                </button>
              ))}
              {/* Mark all as read and Clear all live here, beside the tabs, so the header never wraps
                  (14 Oct 2026). Clear all asks once — there is no undo. */}
              <span className="ml-auto flex shrink-0 items-center gap-1">
                <button onClick={markAllRead} disabled={!unreadCount}
                  className="whitespace-nowrap rounded-lg px-2 py-1.5 text-[13.5px] font-medium enabled:hover:bg-[#F4F2F6]"
                  style={{ color: unreadCount ? '#7A3BD9' : '#B5AFBF', cursor: unreadCount ? 'pointer' : 'default' }}>Mark all as read</button>
                {confirmClear ? <>
                  <button onClick={() => { clearAll(); setConfirmClear(false) }}
                    className="whitespace-nowrap rounded-lg px-2 py-1.5 text-[13.5px] font-semibold text-[#DC2626] hover:bg-[#FDECEA]">Clear all?</button>
                  <button onClick={() => setConfirmClear(false)}
                    className="whitespace-nowrap rounded-lg px-2 py-1.5 text-[13.5px] font-medium text-[#6B6775] hover:bg-[#F4F2F6]">Cancel</button>
                </> : (
                  <button onClick={() => setConfirmClear(true)} disabled={!clearable}
                    className="whitespace-nowrap rounded-lg px-2 py-1.5 text-[13.5px] font-medium enabled:hover:bg-[#F4F2F6]"
                    style={{ color: clearable ? '#4A4653' : '#B5AFBF', cursor: clearable ? 'pointer' : 'default' }}>Clear all</button>
                )}
              </span>
            </div>
            {/* List */}
            <div className="flex-1 overflow-y-auto">
              {shown.length === 0 ? (
                <div className="flex flex-col items-center gap-3 px-8 py-[72px] text-center">
                  <span className="grid h-14 w-14 place-items-center rounded-2xl bg-[#F3EDFC] text-[#7A3BD9]"><Bell className="h-6 w-6" strokeWidth={1.6} /></span>
                  <span className="text-base font-semibold">{tab === 'unread' && notifications.length ? 'You’re all caught up' : 'No notifications yet'}</span>
                  <span className="max-w-[280px] text-[13.5px] leading-normal text-[#6B6775]">New courses, due dates, invitations and finished imports will show up here.</span>
                </div>
              ) : sections.filter(([, items]) => items.length).map(([label, items]) => (
                <div key={label}>
                  <div className="px-6 pb-1.5 pt-4 text-xs font-semibold uppercase tracking-[.06em] text-[#8C8796]">{label}</div>
                  {items.map(n => <Row key={n.id} n={n} now={now}
                    onRead={() => { if (!n.read) markRead(n.id) }}
                    onDismiss={() => remove(n.id)}
                    onGo={n.link ? () => { if (!n.read) markRead(n.id); setOpen(false); navigate(n.link!, { state: { fresh: Date.now() } }) } : undefined}
                    afterAction={() => { if (!n.read) markRead(n.id); setOpen(false) }} />)}
                </div>
              ))}
            </div>
          </aside>
        </div>,
        document.body,
      )}
    </div>
  )
}

/** One notification: tile · title · sub · progress (while running) · time · buttons · dismiss. */
function Row({ n, now, onRead, onDismiss, onGo, afterAction }: {
  n: AppNotification; now: number; onRead: () => void; onDismiss: () => void; onGo?: () => void; afterAction: () => void
}) {
  const k = KIND[n.category] ?? PLAIN
  const Icon = k.icon
  const running = n.status === 'progress'
  const failed = n.status === 'error'
  const red = failed || n.category === 'overdue'
  const known = typeof n.progress === 'number'
  // Buttons: the notification's own (Join / Decline, Open translated course…), else the kind's one
  // way in when it has somewhere to go.
  const own = (n.actions ?? []).filter(a => !(running && a.variant === 'danger' && a.label === 'Cancel'))
  const cancel = running ? (n.actions ?? []).find(a => a.label === 'Cancel') : undefined
  const goLabel = k.action ?? (n.link ? linkLabel(n.link) : undefined)
  const buttons = own.length ? own : onGo && goLabel ? [{ label: goLabel, onClick: onGo, variant: 'primary' as const }] : []
  return (
    <div onClick={onRead} className="group relative flex cursor-pointer gap-3.5 border-b border-[#F4F2F6] py-3.5 pl-6 pr-5 hover:!bg-[#FAF9FB]"
      style={{ background: n.read ? '#fff' : '#FCFAFF' }}>
      {!n.read && <span aria-label="Unread" className="absolute left-[9px] top-7 h-[7px] w-[7px] rounded-full bg-[#7A3BD9]" />}
      <span className="grid h-10 w-10 flex-none place-items-center rounded-xl" style={{ background: failed ? '#FDECEA' : k.bg, color: failed ? '#DC2626' : k.fg }}>
        {failed ? <AlertCircle className="h-5 w-5" strokeWidth={1.8} /> : <Icon className="h-5 w-5" strokeWidth={1.8} />}
      </span>
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <span className="text-[14.5px] leading-[1.4] [text-wrap:pretty]" style={{ fontWeight: n.read ? 500 : 600 }}>{n.title}</span>
        {n.body && <span className="text-[13px] leading-[1.45]" style={{ color: red ? '#B42318' : '#6B6775' }}>{n.body}</span>}
        {running && (
          <div className="flex items-center gap-2.5 pt-1">
            <div className="relative h-1.5 flex-1 overflow-hidden rounded-full bg-[#F1EEF5]">
              {known
                ? <div className="h-full rounded-full transition-[width] duration-[400ms]" style={{ width: `${Math.min(100, n.progress!)}%`, background: k.fg }} />
                : <div className="absolute bottom-0 top-0 w-2/5 rounded-full" style={{ background: k.fg, animation: 'lb-nslide 1.4s ease-in-out infinite' }} />}
            </div>
            {known && <span className="min-w-8 text-right text-xs tabular-nums text-[#6B6775]">{Math.round(n.progress!)}%</span>}
          </div>
        )}
        <span className="text-xs text-[#8C8796]" title={running ? undefined : exact(n.createdAt)}>{running ? 'In progress' : timeAgo(n.createdAt, now)}</span>
        {(buttons.length > 0 || cancel) && (
          <div onClick={e => e.stopPropagation()} className="flex flex-wrap gap-2 pt-1.5">
            {buttons.map((a, i) => (
              <button key={i} onClick={() => { a.onClick(); afterAction() }}
                className={a.variant === 'primary' || (i === 0 && a.variant !== 'ghost' && a.variant !== 'danger')
                  ? 'h-[34px] rounded-[9px] bg-[#7A3BD9] px-[15px] text-[13.5px] font-semibold text-white hover:bg-[#6A2FC4]'
                  : 'h-[34px] rounded-[9px] border border-[#DDD8E4] bg-white px-3.5 text-[13.5px] font-medium hover:bg-[#F4F2F6]'}>
                {a.label}
              </button>
            ))}
            {cancel && (
              <button onClick={() => cancel.onClick()} className="h-[34px] rounded-[9px] border border-[#DDD8E4] bg-white px-3.5 text-[13.5px] font-medium hover:bg-[#F4F2F6]">Cancel</button>
            )}
          </div>
        )}
      </div>
      {!running && (
        <button onClick={e => { e.stopPropagation(); onDismiss() }} aria-label="Dismiss" title="Dismiss"
          className="grid h-7 w-7 flex-none place-items-center rounded-[7px] text-[#B5AFBF] hover:bg-[#F1EEF5] hover:text-[#4A4653]">
          <X className="h-3 w-3" strokeWidth={2} />
        </button>
      )}
    </div>
  )
}
